/**
 * RightSidebar - Modify/Properties Panel
 *
 * BYK-158: Right sidebar for the fixed panel layout
 * Contains modification and inspection tools organized in tabs:
 * - Properties: Selected object properties
 * - AI: AI layout assistant
 * - Render: AI render panel
 * - Issues: Conflict detection panel
 * - Quote: Bill of Materials
 */

import { useMemo, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { PropertiesPanel } from "./PropertiesPanel";
import { AICopilot } from "./AICopilot";
import { RenderPanel } from "./RenderPanel";
import { ConflictsPanel } from "./ConflictsPanel";
import { BOMPanel } from "./BOMPanel";
import { AIErrorBoundary } from "@/components/ErrorBoundary";
import { useFloorPlanStore } from "@/stores/floorPlan";
import { useUIStore, type RightSidebarSection } from "@/stores/uiStore";
import {
  Settings,
  Sparkles,
  Image,
  AlertTriangle,
  FileText,
} from "lucide-react";

// ============================================================================
// Types
// ============================================================================

// Use RightSidebarSection from uiStore for type consistency
type TabId = RightSidebarSection;

interface Tab {
  id: TabId;
  label: string;
  icon: React.ReactNode;
  shortLabel: string;
  badge?: number;
}

// ============================================================================
// Tab Button Component
// ============================================================================

interface TabButtonProps {
  tab: Tab;
  isActive: boolean;
  onClick: () => void;
}

function TabButton({ tab, isActive, onClick }: TabButtonProps) {
  return (
    <button
      id={`right-tab-${tab.id}`}
      onClick={onClick}
      className={cn(
        "relative flex flex-1 flex-col items-center justify-center gap-0.5 px-2 py-2",
        "text-xs font-medium transition-all duration-150",
        "rounded-lg",
        "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset",
        isActive
          ? "bg-blue-50 text-blue-600"
          : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
      )}
      aria-selected={isActive}
      aria-controls={`right-tabpanel-${tab.id}`}
      role="tab"
      tabIndex={isActive ? 0 : -1}
    >
      {tab.icon}
      <span className="hidden text-[10px] sm:inline">{tab.shortLabel}</span>
      {tab.badge !== undefined && tab.badge > 0 && (
        <span
          className="absolute -top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white"
          aria-label={`${tab.badge} ${tab.badge === 1 ? 'item' : 'items'}`}
        >
          {tab.badge > 9 ? "9+" : tab.badge}
        </span>
      )}
    </button>
  );
}

// ============================================================================
// Right Sidebar Component
// ============================================================================

export interface RightSidebarProps {
  /** Custom class name */
  className?: string;
}

export function RightSidebar({
  className,
}: RightSidebarProps) {
  const tabListRef = useRef<HTMLDivElement>(null);

  // Use uiStore for unified state management (activeRightSection also auto-expands sidebar)
  const { activeRightSection, setActiveRightSection } = useUIStore();

  const {
    selectedId,
    conflictResult,
    volumes,
  } = useFloorPlanStore();

  // activeTab comes directly from uiStore - single source of truth
  const activeTab = activeRightSection;

  // Tab clicks go directly to uiStore - which also handles sidebar expansion
  const handleTabClick = useCallback((tabId: TabId) => {
    setActiveRightSection(tabId);
  }, [setActiveRightSection]);

  // Dynamic tabs based on context
  const tabs: Tab[] = useMemo(() => [
    {
      id: "properties",
      label: "Properties",
      shortLabel: "Props",
      icon: <Settings className="h-4 w-4" />,
    },
    {
      id: "ai",
      label: "AI Assistant",
      shortLabel: "AI",
      icon: <Sparkles className="h-4 w-4" />,
    },
    {
      id: "render",
      label: "AI Render",
      shortLabel: "Render",
      icon: <Image className="h-4 w-4" />,
    },
    {
      id: "issues",
      label: "Issues",
      shortLabel: "Issues",
      icon: <AlertTriangle className="h-4 w-4" />,
      badge: conflictResult?.conflicts?.length || 0,
    },
    {
      id: "quote",
      label: "Quote",
      shortLabel: "Quote",
      icon: <FileText className="h-4 w-4" />,
      badge: volumes?.length || 0,
    },
  ], [conflictResult?.conflicts?.length, volumes?.length]);

  // Arrow key navigation for roving tabindex (WCAG 2.1.1)
  const handleTabKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const currentIndex = tabs.findIndex((tab) => tab.id === activeTab);
      let newIndex = currentIndex;

      switch (e.key) {
        case "ArrowLeft":
          e.preventDefault();
          newIndex = currentIndex === 0 ? tabs.length - 1 : currentIndex - 1;
          break;
        case "ArrowRight":
          e.preventDefault();
          newIndex = currentIndex === tabs.length - 1 ? 0 : currentIndex + 1;
          break;
        case "Home":
          e.preventDefault();
          newIndex = 0;
          break;
        case "End":
          e.preventDefault();
          newIndex = tabs.length - 1;
          break;
        default:
          return;
      }

      const newTab = tabs[newIndex];
      handleTabClick(newTab.id);

      // Focus the new tab button
      const tabButton = tabListRef.current?.querySelector(
        `#right-tab-${newTab.id}`
      ) as HTMLButtonElement | null;
      tabButton?.focus();
    },
    [activeTab, tabs, handleTabClick]
  );

  // Auto-switch to properties when something is selected
  // (Commented out to avoid UX disruption - user can click to switch)
  // useEffect(() => {
  //   if (selectedId) {
  //     setActiveTab("properties");
  //   }
  // }, [selectedId]);

  return (
    <aside
      id="right-sidebar"
      aria-label="Properties and settings sidebar"
      className={cn("flex h-full flex-col bg-white", className)}
    >
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 px-4 py-3">
        <h2 className="text-sm font-semibold text-gray-800">Modify</h2>
        <p className="text-xs text-gray-500">
          {selectedId ? "Edit selected element" : "Inspect and modify"}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex-shrink-0 border-b border-gray-200 p-1.5">
        <div
          ref={tabListRef}
          className="flex gap-0.5 rounded-lg bg-gray-100 p-0.5"
          role="tablist"
          aria-label="Properties panel navigation"
          onKeyDown={handleTabKeyDown}
        >
          {tabs.map((tab) => (
            <TabButton
              key={tab.id}
              tab={tab}
              isActive={activeTab === tab.id}
              onClick={() => handleTabClick(tab.id)}
            />
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            id={`right-tabpanel-${activeTab}`}
            role="tabpanel"
            aria-labelledby={`right-tab-${activeTab}`}
            initial={{ opacity: 0, x: 10, pointerEvents: "none" as const }}
            animate={{ opacity: 1, x: 0, pointerEvents: "auto" as const }}
            exit={{ opacity: 0, x: -10, pointerEvents: "none" as const }}
            transition={{ duration: 0.15 }}
            className="h-full"
            tabIndex={0}
          >
            {activeTab === "properties" && <PropertiesPanel />}
            {activeTab === "ai" && (
              <div className="h-full overflow-hidden">
                <AIErrorBoundary componentName="AI Layout Assistant">
                  <AICopilot />
                </AIErrorBoundary>
              </div>
            )}
            {activeTab === "render" && (
              <div className="h-full overflow-y-auto" style={{ overscrollBehaviorY: 'contain' }}>
                <AIErrorBoundary componentName="AI Render">
                  <RenderPanel />
                </AIErrorBoundary>
              </div>
            )}
            {activeTab === "issues" && (
              <div className="h-full overflow-y-auto p-3" style={{ overscrollBehaviorY: 'contain' }}>
                <ConflictsPanel result={conflictResult} />
              </div>
            )}
            {activeTab === "quote" && (
              <div className="h-full overflow-y-auto" style={{ overscrollBehaviorY: 'contain' }}>
                <BOMPanel />
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Context Info Footer */}
      <div className="flex-shrink-0 border-t border-gray-100 bg-gray-50/50 px-3 py-2">
        <div className="flex items-center justify-between text-[11px] text-gray-400">
          <span>
            {volumes.length} item{volumes.length !== 1 ? "s" : ""} placed
          </span>
          {conflictResult?.conflicts?.length > 0 && (
            <span className="flex items-center gap-1 text-amber-500" role="status">
              <AlertTriangle className="h-3 w-3" aria-hidden="true" />
              {conflictResult.conflicts.length} issue
              {conflictResult.conflicts.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>
      </div>
    </aside>
  );
}

export default RightSidebar;
